import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
  OptimizationTile,
  OrphanedTile,
  UnusedTile,
} from '../../reporting/cisco/state-management/models/report-dashboard.model';
import { DashboardService } from '../../reporting/cisco/services/dashboard.service';
import { CustomUiOptionsContext } from '../../shared/contexts/custom-ui-options.context';

export interface DashboardUiState {
  orphanedTile: OrphanedTile;
  optimizationTile: OptimizationTile;
  unusedTile: UnusedTile;
}

export const initialOptimizationTile: OptimizationTile = {
  totalLicenseCount: 0,
  optimizationPercent: 0,
  numCalls: 0,
  tileStatus: {
    message: 'tkey;reports.dashboard.optimization',
    status: null,
    value: 0,
    reportUrl: '../cucm-license-optimization',
    dataAutomation: 'license-optimizer-tile',
  },
  isLoading: true,
};

const initialOrphanedTile: OrphanedTile = {
  isLoading: true,
  orphanedDeviceCount: 0,
  tileStatus: {
    message: 'tkey;reports.dashboard.orphaned_devices',
    status: null,
    value: 0,
    reportUrl: '../orphaned-devices',
    dataAutomation: 'orphaned-devices-tile',
  },
};

const initialUnusedTile: UnusedTile = {
  numCalls: 0,
  unusedCount: 0,
  percentUnused: 0,
  tileStatus: {
    message: 'tkey;reports.dashboard.unused_devices.message.valid',
    status: null,
    value: 0,
    reportUrl: '../device-utilization',
    dataAutomation: 'unused-devices-tile',
  },
  isLoading: true,
};

const initialState: DashboardUiState = {
  orphanedTile: initialOrphanedTile,
  optimizationTile: initialOptimizationTile,
  unusedTile: initialUnusedTile,
};

@Injectable()
export class DashboardContext {
  private _dashboardService = inject(DashboardService);
  private _customUiOptionsContext = inject(CustomUiOptionsContext);
  private _stateSource = new BehaviorSubject<DashboardUiState>(initialState);
  state$ = this._stateSource.asObservable();

  private patchState(patch: Partial<DashboardUiState>): void {
    this._stateSource.next({
      ...this._stateSource.value,
      ...patch,
    });
  }

  loadOptimizationTile(): void {
    this.patchState({ optimizationTile: initialOptimizationTile });

    this._dashboardService.getOptimizationTileValues().subscribe((tile) => {
      this.patchState({ optimizationTile: tile });
    });
  }

  loadOrphanedTile(): void {
    this.patchState({ orphanedTile: initialOrphanedTile });

    this._dashboardService.getOrphanedTileValues().subscribe((tile) => {
      this.patchState({ orphanedTile: tile });
    });
  }

  loadUnusedTile(): void {
    this.patchState({ unusedTile: initialUnusedTile });

    this._customUiOptionsContext.state$
      .pipe(
        map((uiOptions) => uiOptions.reportingEnabled),
        take(1),
        switchMap((isReportsEnabled) => this._dashboardService.getUnusedTileValues(isReportsEnabled)),
      )
      .subscribe((tile) => {
        this.patchState({ unusedTile: tile });
      });
  }
}
